import { useState, useMemo } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Badge,
  Button,
  Checkbox,
  Card,
  CardBody,
  EmptyState as DSEmptyState,
  Skeleton,
} from '@kollektive/design-system';
import { Trash2, Users, AlertTriangle, Search, Pencil, Crown, Shield, User, Eye, EyeOff, Loader2 } from 'lucide-react';
import type { TeamMember, MemberRevealResponse } from '@/hooks/useTeam';
import styles from './MemberTable.module.css';

interface MemberTableProps {
  members: TeamMember[];
  currentUserId: string;
  currentUserRole: string;
  isLoading: boolean;
  error: Error | null;
  onRetry?: () => void;
  onEdit: (member: TeamMember) => void;
  onRemove: (member: TeamMember) => void;
  onBulkRemove: (members: TeamMember[]) => void;
  onReveal: (memberId: string) => Promise<MemberRevealResponse>;
  isRemoving?: boolean;
  searchQuery?: string;
}

type SortField = 'name' | 'email' | 'role' | 'created_at';
type SortDirection = 'asc' | 'desc';

// Helper functions
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

type RoleVariant = 'brand' | 'warning' | 'info' | 'neutral';

const roleConfig: Record<string, { label: string; variant: RoleVariant; icon: typeof Crown }> = {
  owner: { label: 'Owner', variant: 'brand', icon: Crown },
  admin: { label: 'Admin', variant: 'warning', icon: Shield },
  member: { label: 'Member', variant: 'info', icon: User },
  viewer: { label: 'Viewer', variant: 'neutral', icon: Eye },
};

// Skeleton component for loading state
function TableSkeleton() {
  return (
    <Card>
      <CardBody className={styles.cardBody}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader style={{ width: 40 }}><Skeleton width={16} height={16} /></TableHeader>
              <TableHeader>Name</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Role</TableHeader>
              <TableHeader>Joined</TableHeader>
              <TableHeader style={{ width: 100 }}></TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3].map((i) => (
              <TableRow key={i}>
                <TableCell><Skeleton width={16} height={16} /></TableCell>
                <TableCell><Skeleton width="8rem" height="1rem" /></TableCell>
                <TableCell><Skeleton width="10rem" height="1rem" /></TableCell>
                <TableCell><Skeleton width="4rem" height="1.25rem" /></TableCell>
                <TableCell><Skeleton width="5rem" height="1rem" /></TableCell>
                <TableCell>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Skeleton width={24} height={24} />
                    <Skeleton width={24} height={24} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardBody>
    </Card>
  );
}

export function MemberTable({
  members,
  currentUserId,
  currentUserRole,
  isLoading,
  error,
  onRetry,
  onEdit,
  onRemove,
  onBulkRemove,
  onReveal,
  isRemoving,
  searchQuery = '',
}: MemberTableProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [revealedMembers, setRevealedMembers] = useState<Record<string, MemberRevealResponse>>({});
  const [revealingIds, setRevealingIds] = useState<Set<string>>(new Set());

  // Filter members by search query
  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return members;

    const query = searchQuery.toLowerCase();
    return members.filter((member) =>
      member.name?.toLowerCase().includes(query) ||
      member.email?.toLowerCase().includes(query) ||
      member.role.toLowerCase().includes(query)
    );
  }, [members, searchQuery]);

  // Sort filtered members
  const sortedMembers = useMemo(() => {
    return [...filteredMembers].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortField) {
        case 'name':
          aVal = a.name || '';
          bVal = b.name || '';
          break;
        case 'email':
          aVal = a.email || '';
          bVal = b.email || '';
          break;
        case 'role':
          // Sort order: owner > admin > member > viewer
          const roleOrder = { owner: 0, admin: 1, member: 2, viewer: 3 };
          aVal = roleOrder[a.role as keyof typeof roleOrder] ?? 99;
          bVal = roleOrder[b.role as keyof typeof roleOrder] ?? 99;
          break;
        case 'created_at':
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortDirection === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });
  }, [filteredMembers, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Can only select members that can be removed (not owner, not self)
  const selectableMembers = sortedMembers.filter(
    (m) => m.role !== 'owner' && m.id !== currentUserId
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(selectableMembers.map((m) => m.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (memberId: string, checked: boolean) => {
    const newSet = new Set(selectedIds);
    if (checked) {
      newSet.add(memberId);
    } else {
      newSet.delete(memberId);
    }
    setSelectedIds(newSet);
  };

  const handleBulkRemove = () => {
    const membersToRemove = members.filter((m) => selectedIds.has(m.id));
    onBulkRemove(membersToRemove);
    setSelectedIds(new Set());
  };

  const canEditMember = (member: TeamMember): boolean => {
    // Owner can edit anyone except themselves (for role changes)
    // Admin can edit member/viewer roles
    if (member.id === currentUserId) return false;
    if (member.role === 'owner') return false;
    if (currentUserRole === 'owner') return true;
    if (currentUserRole === 'admin' && (member.role === 'member' || member.role === 'viewer')) {
      return true;
    }
    return false;
  };

  const canRemoveMember = (member: TeamMember): boolean => {
    // Owner cannot be removed
    // Cannot remove yourself
    // Same logic as canEditMember for permissions
    if (member.id === currentUserId) return false;
    if (member.role === 'owner') return false;
    if (currentUserRole === 'owner') return true;
    if (currentUserRole === 'admin' && (member.role === 'member' || member.role === 'viewer')) {
      return true;
    }
    return false;
  };

  const handleReveal = async (memberId: string) => {
    // If already revealed, toggle back to masked
    if (revealedMembers[memberId]) {
      setRevealedMembers((prev) => {
        const newState = { ...prev };
        delete newState[memberId];
        return newState;
      });
      return;
    }

    // Reveal the member's PII
    setRevealingIds((prev) => new Set(prev).add(memberId));
    try {
      const revealed = await onReveal(memberId);
      setRevealedMembers((prev) => ({ ...prev, [memberId]: revealed }));
    } catch (err) {
      console.error('Failed to reveal member PII:', err);
    } finally {
      setRevealingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(memberId);
        return newSet;
      });
    }
  };

  const allSelected = selectableMembers.length > 0 && selectedIds.size === selectableMembers.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < selectableMembers.length;

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (error) {
    return (
      <DSEmptyState
        variant="error"
        icon={<AlertTriangle size={48} />}
        title="Failed to load team members"
        description={error.message}
        action={onRetry ? <Button onClick={onRetry}>Try again</Button> : undefined}
      />
    );
  }

  if (members.length === 0) {
    return (
      <DSEmptyState
        icon={<Users size={48} />}
        title="No team members"
        description="Invite team members to collaborate on your workspace."
      />
    );
  }

  return (
    <div className={styles.container}>
      {filteredMembers.length > 0 ? (
        <Card className={styles.tableCard}>
          <CardBody className={styles.cardBody}>
            <Table hoverable>
              <TableHead>
                <TableRow>
                  <TableHeader style={{ width: 40 }} className={styles.checkboxCell}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      disabled={selectableMembers.length === 0}
                    />
                  </TableHeader>
                  <TableHeader
                    sortable
                    sortDirection={sortField === 'name' ? sortDirection : null}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableHeader>
                  <TableHeader
                    sortable
                    sortDirection={sortField === 'email' ? sortDirection : null}
                    onClick={() => handleSort('email')}
                  >
                    Email
                  </TableHeader>
                  <TableHeader
                    sortable
                    sortDirection={sortField === 'role' ? sortDirection : null}
                    onClick={() => handleSort('role')}
                  >
                    Role
                  </TableHeader>
                  <TableHeader
                    sortable
                    sortDirection={sortField === 'created_at' ? sortDirection : null}
                    onClick={() => handleSort('created_at')}
                  >
                    Joined
                  </TableHeader>
                  <TableHeader style={{ width: 100 }}></TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedMembers.map((member) => {
                  const defaultConfig = { label: member.role, variant: 'neutral' as const, icon: User };
                  const config = roleConfig[member.role] ?? defaultConfig;
                  const RoleIcon = config.icon;
                  const isCurrentUser = member.id === currentUserId;
                  const canSelect = member.role !== 'owner' && !isCurrentUser;

                  return (
                    <TableRow key={member.id} selected={selectedIds.has(member.id)}>
                      <TableCell className={styles.checkboxCell}>
                        <Checkbox
                          checked={selectedIds.has(member.id)}
                          onChange={(e) => handleSelectOne(member.id, e.target.checked)}
                          disabled={!canSelect}
                        />
                      </TableCell>
                      <TableCell>
                        <div className={styles.nameCell}>
                          <span className={styles.memberName}>
                            {member.name}
                            {isCurrentUser && <span className={styles.youBadge}>(you)</span>}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={styles.emailCell}>
                          <span className={styles.email}>
                            {revealedMembers[member.id]?.email ?? member.email}
                          </span>
                          {member.is_encrypted && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleReveal(member.id)}
                              disabled={revealingIds.has(member.id)}
                              title={revealedMembers[member.id] ? 'Hide email' : 'Reveal full email'}
                              className={styles.revealButton}
                            >
                              {revealingIds.has(member.id) ? (
                                <Loader2 size={14} className={styles.spinning} />
                              ) : revealedMembers[member.id] ? (
                                <EyeOff size={14} />
                              ) : (
                                <Eye size={14} />
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={config.variant} styleVariant="subtle" size="sm">
                          <RoleIcon size={12} />
                          {config.label}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(member.created_at)}</TableCell>
                      <TableCell>
                        <div className={styles.actions}>
                          {canEditMember(member) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onEdit(member)}
                              title="Edit member"
                              className={styles.actionButton}
                            >
                              <Pencil size={16} />
                            </Button>
                          )}
                          {canRemoveMember(member) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onRemove(member)}
                              disabled={isRemoving}
                              title="Remove member"
                              className={styles.removeButton}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      ) : (
        <Card className={styles.tableCard}>
          <CardBody>
            <DSEmptyState
              variant="search"
              icon={<Search size={48} />}
              title="No results"
              description={`No team members match "${searchQuery}"`}
            />
          </CardBody>
        </Card>
      )}

      {/* Bulk actions bar at bottom */}
      <div className={`${styles.bulkActions} ${selectedIds.size === 0 ? styles.bulkActionsHidden : ''}`}>
        <span className={styles.selectedCount}>
          {selectedIds.size} selected
        </span>
        <Button
          variant="danger"
          size="sm"
          onClick={handleBulkRemove}
          disabled={isRemoving}
          iconLeft={<Trash2 size={16} />}
        >
          Remove Selected
        </Button>
      </div>
    </div>
  );
}
